import { buildTimeline, errorCode, unreadAfterAnchor, type MessageView, type MessengerClient, type TimelineItem } from '@sd/client-core';
import { compareSeq, LIMITS, type ConversationSummary, type EditWindow, type Person, type Seq } from '@sd/contracts';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { formatClock, formatDayDivider } from '../format';
import { Icon } from '../icons/Icon';
import { Avatar } from '../ui/Avatar';
import { Button } from '../ui/controls';
import { LONG_PRESS_MS } from '../ui/keys';
import { useConfirm, usePopover, useToast } from '../ui/overlays';
import { FileCard, ImageGrid, isPreviewTile, UploadCard } from './attachments';
import { LoadFailed, RichText, usePerson } from './bits';
import { plainText } from './markup';
import { LinkPreviewCards } from './LinkPreviews';
import { useAppActions, useClient, useClientState } from './context';
import { errorMessage } from '../errors';
import { arrivalText, editWindowText, isSelfChat, messageLabel, reactionFailedMessage, sendStatusText, systemIcon, systemText } from './copy';
import { ReactionChips, ReactionPicker, ReactorsPopover } from './reactions';
import { useComposerActions, useShell } from './shell';
import { arrivedAtBottom, firstContentKey, openingPosition } from './timelineScroll';

const WINDOW_MS: Record<EditWindow, number> = { '1h': 3_600_000, '24h': 86_400_000, '7d': 604_800_000, none: Number.POSITIVE_INFINITY };
/** Room left above the unread divider when a conversation opens there. */
const DIVIDER_GAP_PX = 12;

/** Rendered in the popover layer (outside the client context), so the client is passed in. */
function ReadersPopover({ client, conversationId, messageId }: { client: MessengerClient; conversationId: string; messageId: string }) {
  const [data, setData] = useState<{ read: Person[]; unread: Person[] } | 'failed' | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let alive = true;
    client
      .readers(conversationId, messageId)
      .then((d) => alive && setData(d))
      .catch(() => alive && setData('failed'));
    return () => {
      alive = false;
    };
  }, [client, conversationId, messageId, attempt]);
  if (data === 'failed')
    return (
      <LoadFailed
        onRetry={() => {
          setData(null);
          setAttempt((n) => n + 1);
        }}
      />
    );
  if (!data) return <h2>불러오는 중…</h2>;
  return (
    <>
      <h2>읽음 {data.read.length}</h2>
      {data.read.map((p) => (
        <div key={p.id} className="pi">
          <Avatar person={p} noDot />
          {p.displayName}
          <small>읽음</small>
        </div>
      ))}
      <hr />
      <h2>안 읽음 {data.unread.length}</h2>
      {data.unread.map((p) => (
        <div key={p.id} className="pi" style={{ opacity: 0.7 }}>
          <Avatar person={p} noDot />
          {p.displayName}
          <small>안 읽음</small>
        </div>
      ))}
    </>
  );
}

interface RowProps {
  item: Extract<TimelineItem, { kind: 'message' }>;
  conversation: ConversationSummary;
  mentionNames: string[];
  editWindowMs: number;
  /** "24시간 이내", or null when the company allows edits at any time. */
  editLimit: string | null;
  /** Roving tabindex: the one message Tab stops at in the timeline. */
  current: boolean;
  /** Came in at the bottom while the conversation was shown: it rises into place. */
  arrived: boolean;
  onFocusRow(key: string): void;
}

/** Room the hover actions take above a message line (`.hacts` sits at top: -34px). */
const HACTS_ROOM_PX = 34;

/**
 * The hover actions sit above the message line, where the scrolling timeline cuts them off when the
 * line is at its top edge: there they go below the whole message instead (`.row-body[data-hacts-below]`),
 * clear of its reactions, cards and photos, and stay while the pointer crosses those to reach them.
 * Pointer moves follow the line as the list scrolls under a resting pointer.
 */
function placeActions(event: React.SyntheticEvent<HTMLDivElement>) {
  const body = event.currentTarget;
  const line = body.querySelector(':scope > .row-line');
  const list = body.closest('.msgs');
  if (!line || !list) return;
  const below = line.getBoundingClientRect().top - list.getBoundingClientRect().top < HACTS_ROOM_PX;
  if (below !== body.hasAttribute('data-hacts-below')) body.toggleAttribute('data-hacts-below', below);
}

/** Arrow keys move between messages like the rows of a list; Home and End go to the first and last loaded one. */
function moveBetweenRows(event: ReactKeyboardEvent<HTMLElement>): boolean {
  if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return false;
  const rows = [...(event.currentTarget.parentElement?.children ?? [])].filter((el): el is HTMLElement => el instanceof HTMLElement && el.classList.contains('row'));
  const index = rows.indexOf(event.currentTarget);
  const target = event.key === 'Home' ? rows[0] : event.key === 'End' ? rows[rows.length - 1] : rows[index + (event.key === 'ArrowUp' ? -1 : 1)];
  event.preventDefault();
  // focus() alone leaves a row half hidden at the edge of the list.
  target?.focus({ preventScroll: true });
  target?.scrollIntoView({ block: 'nearest' });
  return true;
}

/** The conversation as a row uses it (its id, kind, whether one may write, and SendStatus's 나와의 채팅 test). */
const sameForRows = (a: ConversationSummary, b: ConversationSummary) =>
  a === b || (a.id === b.id && a.kind === b.kind && a.readOnly === b.readOnly && a.readOnlyReason === b.readOnlyReason && a.memberCount === b.memberCount && a.peer?.id === b.peer?.id);

// memo: a row is drawn again only when its own props change, not whenever the timeline or the shell does (opening the
// info panel, a reply being chosen, a new message changing the conversation's preview and unread count, a reconnect);
// everything else it shows comes from the store.
const MessageRow = memo(function MessageRow({ item, conversation, mentionNames, editWindowMs, editLimit, current, arrived, onFocusRow }: RowProps) {
  const { message: m, position } = item;
  const client = useClient();
  const env = useAppActions();
  const composer = useComposerActions();
  const popover = usePopover();
  const confirm = useConfirm();
  const toast = useToast();
  const sender = usePerson(m.senderId);
  const people = useClientState((s) => s.people);
  const rowRef = useRef<HTMLDivElement>(null);
  const meId = useClientState((s) => s.me?.id ?? '');
  const reactionsOn = useClientState((s) => s.serverInfo?.capabilities.includes('reactions.v1') ?? false);
  const confirmed = Boolean(m.id);
  const canReply = confirmed && !conversation.readOnly;
  const canDelete = m.mine && confirmed && !m.deleted && env.now().getTime() - new Date(m.createdAt).getTime() < editWindowMs && !conversation.readOnly;
  // A message with only attachments can be deleted but has no text to edit.
  const canEdit = canDelete && Boolean(m.text);
  const canReact = reactionsOn && confirmed && !m.deleted && !conversation.readOnly;
  const forwardOn = useClientState((s) => s.serverInfo?.capabilities.includes('messages.forward') ?? false);
  // Also from a read-only conversation: what was readable can go elsewhere.
  const canForward = forwardOn && confirmed && !m.deleted && (Boolean(m.text) || m.attachments.length > 0);
  const savedOn = useClientState((s) => s.serverInfo?.capabilities.includes('messages.saved') ?? false);
  const saved = useClientState((s) => Boolean(m.id && s.saved[m.id]));
  const canSave = savedOn && confirmed && !m.deleted;
  const hasMenu = !m.deleted && (canReply || Boolean(m.text) || canDelete || canReact || canForward || canSave || m.reactions.length > 0);

  // Deleting removes the menu button and actions that held focus: keep the keyboard on the message.
  const refocusOnDelete = useRef(false);
  useLayoutEffect(() => {
    if (!m.deleted || !refocusOnDelete.current) return;
    refocusOnDelete.current = false;
    if (document.activeElement === document.body) rowRef.current?.focus();
  }, [m.deleted]);

  const reply = () => {
    if (!m.id) return;
    composer.setReplyTo(m.id);
    popover.close();
    composer.focus();
  };
  const toggleSaved = () => {
    popover.close();
    if (!m.id) return;
    client.setSaved(conversation.id, m.id, !saved).then(
      () => toast(saved ? '저장을 취소했어요' : '저장했어요 · 검색의 ‘저장’ 탭에서 다시 볼 수 있어요'),
      (error: unknown) => toast(errorCode(error) === 'CONFLICT' ? `저장은 ${LIMITS.maxSavedMessages}개까지 할 수 있어요` : errorMessage(error)),
    );
  };
  const forward = () => {
    popover.close();
    if (m.id) env.openModal({ kind: 'forward', conversationId: conversation.id, messageId: m.id });
  };
  const copy = () => {
    void navigator.clipboard?.writeText(m.text ?? '').catch(() => {});
    popover.close();
    toast('메시지를 복사했어요');
  };
  const edit = () => {
    if (!m.id) return;
    composer.startEdit(m.id);
    popover.close();
  };
  const remove = async () => {
    popover.close();
    if (!m.id) return;
    const ok = await confirm({
      title: '메시지를 삭제할까요?',
      body: `대화·검색·다운로드에서 바로 숨겨지고 ‘삭제된 메시지예요’로 표시돼요. 본문은 ${LIMITS.deletedContentGraceDays}일 뒤 서버에서 완전히 지워져요.`,
      ok: '삭제',
      danger: true,
    });
    if (!ok) return;
    refocusOnDelete.current = true;
    try {
      await client.deleteMessage(conversation.id, m.id);
      toast('메시지를 삭제했어요');
    } catch (error) {
      refocusOnDelete.current = false;
      toast(errorMessage(error));
    }
  };
  const toggleReaction = (emoji: string) => {
    if (!m.id) return;
    const adding = !m.reactions.some((r) => r.emoji === emoji && r.me);
    client.toggleReaction(conversation.id, m.id, emoji).catch((error: unknown) => toast(reactionFailedMessage(errorCode(error), adding)));
  };
  const openPicker = (anchor: HTMLElement) =>
    popover.open(
      anchor,
      (close) => (
        <ReactionPicker
          mine={m.reactions.filter((r) => r.me).map((r) => r.emoji)}
          onPick={(emoji) => {
            close();
            toggleReaction(emoji);
          }}
        />
      ),
      { align: m.mine ? 'right' : 'left', role: 'dialog', label: '반응 추가' },
    );
  const openReactors = (anchor: HTMLElement) =>
    m.id && popover.open(anchor, () => <ReactorsPopover client={client} conversationId={conversation.id} messageId={m.id!} meId={meId} />, { align: m.mine ? 'right' : 'left', role: 'dialog', label: '반응한 사람' });

  const images = m.attachments.filter(isPreviewTile);
  const files = m.attachments.filter((a) => !isPreviewTile(a));
  const hasText = Boolean(m.text);
  let body: React.ReactNode;
  if (m.deleted) {
    body = (
      <div className="bubble deleted">
        <Icon name="trash" size="sm" />
        삭제된 메시지예요
      </div>
    );
  } else {
    const original = m.reply;
    const quote = !original ? null : original.state === 'available' ? (
      // The message replied to: pressing it shows that message, loaded around it when it is further up.
      <button
        type="button"
        className="quote jump"
        tabIndex={current ? 0 : -1}
        aria-label={`답장한 메시지로 이동: ${people[original.senderId ?? '']?.displayName ?? ''} ${original.excerpt ? plainText(original.excerpt) : (original.attachmentName ?? '')}`}
        onClick={() => client.openConversation(conversation.id, { messageId: original.messageId }).catch((error: unknown) => toast(errorMessage(error)))}
      >
        <Icon name="reply" size="xs" />
        <span className="ell">
          <b>{people[original.senderId ?? '']?.displayName ?? ''}</b> {original.excerpt ? plainText(original.excerpt) : (original.attachmentName ?? '')}
        </span>
      </button>
    ) : (
      <div className="quote">
        <Icon name="reply" size="xs" />
        {original.state === 'deleted' ? '삭제된 메시지에 대한 답장' : '볼 수 없는 메시지에 대한 답장'}
      </div>
    );
    const cls = ['bubble', 'tail', m.mentionsMe && 'mention', m.state === 'failed' && 'failed', m.state === 'queued' && 'queued'].filter(Boolean).join(' ');
    const forwarded = m.forwarded ? (
      <div className="fwd">
        <Icon name="reply" size="xs" className="flip" />
        전달됨
      </div>
    ) : null;
    const textBubble =
      hasText || quote || m.upload ? (
        <div className={cls}>
          {forwarded}
          {quote}
          {m.text ? <RichText text={m.text} mentionNames={mentionNames} tabbable={current} /> : null}
          {m.edited ? (
            <>
              {' '}
              <span className="edited">(수정됨)</span>
            </>
          ) : null}
          {m.upload ? (
            <UploadCard
              upload={m.upload}
              tabbable={current}
              onCancel={() => {
                if (!m.clientMessageId) return;
                client.discard(m.clientMessageId);
                composer.insert(m.text ?? '');
                toast('업로드를 취소했어요. 작성한 내용은 입력창에 돌려놨어요');
              }}
            />
          ) : null}
        </div>
      ) : null;
    const previews = m.linkPreviews.length ? <LinkPreviewCards view={m} conversation={conversation} tabbable={current} /> : null;
    const attachments = (
      <>
        {images.length ? <ImageGrid files={images} tabbable={current} /> : null}
        {files.map((f) => (
          <FileCard key={f.id} file={f} tabbable={current} />
        ))}
      </>
    );
    body =
      textBubble && (m.attachments.length || previews) ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: m.mine ? 'flex-end' : 'flex-start', minWidth: 0 }}>
          {textBubble}
          {previews}
          {attachments}
        </div>
      ) : textBubble ? (
        textBubble
      ) : (
        <>
          {forwarded ? <div className={`fwd-files${m.mine ? ' mine' : ''}`}>{forwarded}</div> : null}
          {attachments}
        </>
      );
  }

  const status = m.mine ? <SendStatus view={m} conversation={conversation} tabbable={current} /> : null;
  const savedMark = saved ? <Icon name="note" size="xs" className="saved-mark" /> : null;
  const meta = m.mine ? (
    <div className="meta">
      {savedMark}
      {status}
      <span>{formatClock(m.createdAt)}</span>
    </div>
  ) : item.showTime || saved ? (
    <div className="meta">
      {savedMark}
      {item.showTime ? <span>{formatClock(m.createdAt)}</span> : null}
    </div>
  ) : null;

  // Hover shortcuts for the pointer; keyboard and screen reader users get the same actions from the menu.
  const actions = m.deleted ? null : (
    <div className="hacts" role="toolbar" aria-label="메시지 동작">
      {canReact ? (
        <button type="button" tabIndex={-1} onClick={(e) => openPicker(e.currentTarget)} data-tip="반응 추가" aria-label="반응 추가" aria-haspopup="dialog">
          <Icon name="smile" />
        </button>
      ) : null}
      {canReply ? (
        <button type="button" tabIndex={-1} onClick={reply} data-tip="답장" aria-label="답장">
          <Icon name="reply" />
        </button>
      ) : null}
      {m.text ? (
        <button type="button" tabIndex={-1} onClick={copy} data-tip="복사" aria-label="복사">
          <Icon name="copy" />
        </button>
      ) : null}
      {canEdit ? (
        <button type="button" tabIndex={-1} onClick={edit} data-tip={editLimit ? `수정 · 보낸 후 ${editLimit}` : '수정'} aria-label="수정">
          <Icon name="compose" />
        </button>
      ) : null}
      {canDelete ? (
        <button type="button" tabIndex={-1} className="del" onClick={() => void remove()} data-tip={editLimit ? `삭제 · 보낸 후 ${editLimit}` : '삭제'} aria-label="삭제">
          <Icon name="trash" />
        </button>
      ) : null}
    </div>
  );
  const openMenu = (anchor: HTMLElement) =>
    popover.open(
      anchor,
      () => (
        <>
          {canReply ? (
            <button type="button" role="menuitem" className="pi" onClick={reply}>
              <Icon name="reply" size="sm" />
              답장
            </button>
          ) : null}
          {m.text ? (
            <button type="button" role="menuitem" className="pi" onClick={copy}>
              <Icon name="copy" size="sm" />
              복사
            </button>
          ) : null}
          {canReact ? (
            <button type="button" role="menuitem" className="pi" aria-haspopup="dialog" onClick={() => openPicker(anchor)}>
              <Icon name="smile" size="sm" />
              반응 추가
            </button>
          ) : null}
          {m.reactions.length ? (
            <button type="button" role="menuitem" className="pi" aria-haspopup="dialog" onClick={() => openReactors(anchor)}>
              <Icon name="users" size="sm" />
              반응한 사람 보기
            </button>
          ) : null}
          {canForward ? (
            <button type="button" role="menuitem" className="pi" aria-haspopup="dialog" onClick={forward}>
              <Icon name="reply" size="sm" className="flip" />
              전달
            </button>
          ) : null}
          {canSave ? (
            <button type="button" role="menuitem" className="pi" onClick={toggleSaved}>
              <Icon name="note" size="sm" />
              {saved ? '저장 취소' : '저장'}
            </button>
          ) : null}
          {canEdit ? (
            <button type="button" role="menuitem" className="pi" onClick={edit}>
              <Icon name="compose" size="sm" />
              수정 {editLimit ? <small>{editLimit}</small> : null}
            </button>
          ) : null}
          {canDelete ? (
            <button type="button" role="menuitem" className="pi danger" onClick={() => void remove()}>
              <Icon name="trash" size="sm" />
              삭제 {editLimit ? <small>{editLimit}</small> : null}
            </button>
          ) : null}
        </>
      ),
      { align: m.mine ? 'right' : 'left', role: 'menu', label: '메시지 메뉴' },
    );
  const more = hasMenu ? (
    <button type="button" className="more-btn" aria-label="메시지 메뉴" aria-haspopup="menu" tabIndex={current ? 0 : -1} onClick={(e) => openMenu(e.currentTarget)}>
      <Icon name="more" />
    </button>
  ) : null;

  // On a touch screen holding a message opens its menu, as phone messengers do (its ••• button stays for screen
  // readers). Moving the finger (a scroll) or lifting it early lets go; links and buttons keep their own press.
  const press = useRef<{ timer: number; x: number; y: number; line: HTMLElement } | null>(null);
  const letGo = () => {
    if (!press.current) return;
    window.clearTimeout(press.current.timer);
    press.current.line.classList.remove('pressing');
    press.current = null;
  };
  useEffect(
    () => () => {
      if (press.current) window.clearTimeout(press.current.timer);
    },
    [],
  );
  const holdHandlers = hasMenu
    ? {
        onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => {
          if (e.pointerType === 'mouse' || (e.target as Element).closest('a,button,input,textarea,video')) return;
          letGo();
          const line = e.currentTarget;
          line.classList.add('pressing');
          const timer = window.setTimeout(() => {
            letGo();
            navigator.vibrate?.(10);
            openMenu(line.querySelector<HTMLElement>(':scope > .bubble, :scope > .fcard, :scope > .imgs') ?? line);
          }, LONG_PRESS_MS);
          press.current = { timer, x: e.clientX, y: e.clientY, line };
        },
        onPointerMove: (e: ReactPointerEvent<HTMLDivElement>) => {
          if (press.current && Math.hypot(e.clientX - press.current.x, e.clientY - press.current.y) > 8) letGo();
        },
        onPointerUp: letGo,
        onPointerCancel: letGo,
        // Android's own long-press menu (copy, select) would open over ours.
        onContextMenu: (e: ReactMouseEvent<HTMLDivElement>) => {
          if (e.nativeEvent instanceof PointerEvent ? e.nativeEvent.pointerType !== 'mouse' : press.current) e.preventDefault();
        },
      }
    : {};

  const grp = position === 'single' ? 'grp-first grp-last' : `grp-${position}`;
  return (
    <div
      ref={rowRef}
      className={`row ${m.mine ? 'mine' : 'other'} ${grp}${position === 'single' || position === 'first' ? ' first' : ''}${arrived ? ' arrive' : ''}`}
      data-mid={m.id ?? m.clientMessageId ?? undefined}
      role="article"
      aria-roledescription="메시지"
      aria-label={messageLabel(m, {
        sender: m.mine ? '나' : (sender?.displayName ?? '알 수 없는 사용자'),
        status: m.mine ? sendStatusText(m, conversation) : null,
        people: (id) => (id ? people[id] : undefined),
        meId,
        now: env.now(),
        saved,
      })}
      tabIndex={current ? 0 : -1}
      onFocus={(e) => e.target === e.currentTarget && onFocusRow(item.key)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget || moveBetweenRows(e)) return;
        // Enter, the context-menu key or Shift+F10 open the message menu without tabbing to its button.
        if (hasMenu && (e.key === 'Enter' || e.key === 'ContextMenu' || (e.key === 'F10' && e.shiftKey))) {
          e.preventDefault();
          openMenu(e.currentTarget);
        }
      }}
    >
      {m.mine ? null : <div className="row-av">{item.showAvatar ? <Avatar person={sender} noDot /> : null}</div>}
      <div className="row-body" onPointerEnter={placeActions} onPointerMove={placeActions} onFocus={placeActions}>
        {item.showName ? <div className="row-name">{sender?.displayName ?? ''}</div> : <span className="sr">{m.mine ? '나' : (sender?.displayName ?? '')}</span>}
        <div className="row-line" {...holdHandlers}>
          {body}
          {meta}
          {more}
          {actions}
        </div>
        <ReactionChips
          reactions={m.reactions}
          people={(id) => (id ? people[id] : undefined)}
          meId={meId}
          canReact={canReact}
          tabbable={current}
          onToggle={toggleReaction}
          onAdd={openPicker}
        />
        {m.state === 'failed' && m.clientMessageId ? (
          <div className="fail-acts">
            {m.lastError && !['INTERNAL', 'NETWORK', 'MAINTENANCE', 'RATE_LIMITED', 'FAILED_BEFORE_RESTART'].includes(m.lastError) ? (
              <span className="hint" style={{ margin: '0 auto 0 0', alignSelf: 'center' }}>
                {errorMessage(m.lastError)}
              </span>
            ) : null}
            <Button className="retry" icon="retry" tabIndex={current ? undefined : -1} onClick={() => client.retry(m.clientMessageId!)}>
              다시 보내기
            </Button>
            <Button
              variant="ghost"
              tabIndex={current ? undefined : -1}
              onClick={() => {
                client.discard(m.clientMessageId!);
                toast('보내지 못한 메시지를 지웠어요');
              }}
            >
              삭제
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}, (prev, next) =>
  prev.item === next.item &&
  sameForRows(prev.conversation, next.conversation) &&
  prev.mentionNames === next.mentionNames &&
  prev.editWindowMs === next.editWindowMs &&
  prev.editLimit === next.editLimit &&
  prev.current === next.current &&
  prev.arrived === next.arrived &&
  prev.onFocusRow === next.onFocusRow,
);

function SendStatus({ view, conversation, tabbable }: { view: MessageView; conversation: ConversationSummary; tabbable: boolean }) {
  const popover = usePopover();
  const client = useClient();
  const text = sendStatusText(view, conversation);
  // Nobody else reads a conversation with oneself: only a send still on its way (or failed) says anything.
  if (isSelfChat(conversation) && (view.state === 'sent' || view.state === 'read')) return null;
  switch (view.state) {
    case 'queued':
      return (
        <span className="st queued">
          <Icon name="clock" />
          {text}
        </span>
      );
    case 'sending':
      return (
        <span className="st sending">
          <Icon name="sync" className="spin" />
          {text}
        </span>
      );
    case 'sent':
      return (
        <span className="st sent" data-tip="서버에 저장됐어요" data-tip-pos="left">
          <Icon name="check" />
          {text}
        </span>
      );
    case 'read': {
      const receipt = view.receipt;
      if (conversation.kind === 'direct' || (receipt && receipt.eligibleCount > 0 && receipt.readCount >= receipt.eligibleCount))
        return (
          <span className="st read">
            <Icon name="check2" />
            {text}
          </span>
        );
      return (
        <button
          type="button"
          className="st read btnlike"
          aria-label={`${text}, 목록 보기`}
          aria-haspopup="dialog"
          tabIndex={tabbable ? undefined : -1}
          onClick={(e) =>
            view.id && popover.open(e.currentTarget, () => <ReadersPopover client={client} conversationId={conversation.id} messageId={view.id!} />, { align: 'right', role: 'dialog', label: '읽은 사람' })
          }
        >
          <Icon name="check2" />
          읽음 {receipt?.readCount ?? 0}
        </button>
      );
    }
    case 'failed':
      return (
        <span className="st failed">
          <Icon name="alertc" />
          {text}
        </span>
      );
    default:
      return null;
  }
}

/** The scrollable message timeline (`.msgs`) with the "새 메시지" jump button. */
export function Timeline({ conversation }: { conversation: ConversationSummary }) {
  const client = useClient();
  const shell = useShell();
  const env = useAppActions();
  const toast = useToast();
  const timeline = useClientState((s) => s.timelines[conversation.id]);
  const pendingAll = useClientState((s) => s.pending);
  const pendingReactions = useClientState((s) => s.pendingReactions);
  const people = useClientState((s) => s.people);
  const meId = useClientState((s) => s.me?.id ?? '');
  const editWindow = useClientState((s) => s.serverInfo?.limits.editWindow ?? '24h');
  const flash = useClientState((s) => s.flash);
  const details = useClientState((s) => s.details[conversation.id]);
  const box = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const positioned = useRef(false);
  const firstKey = useRef<string | null>(null);
  const prevHeight = useRef(0);
  const flashed = useRef<number | null>(null);
  /** Message keys drawn so far (null until the conversation is positioned), and those that came in after. */
  const known = useRef<ReadonlySet<string> | null>(null);
  const arrived = useRef(new Set<string>());
  /** Gliding to the newest message: the scroll events on the way still count as at the bottom. */
  const gliding = useRef(false);
  const glideTimer = useRef(0);
  /** Until when a glide to a found message (a reply's original) is on its way: the list is leaving the bottom. */
  const seeking = useRef(0);
  const resizes = useRef<ResizeObserver | null>(null);
  /** The list's height when last measured: a change is the box resizing (a phone keyboard), not the reader scrolling. */
  const boxHeight = useRef(0);
  /** "최근 메시지로" was pressed on a slice: show the bottom once the newest page is in. */
  const toBottom = useRef(false);
  const [far, setFar] = useState(false);
  /** Key of the message Tab returns to; the newest one until the reader moves. */
  const [focusKey, setFocusKey] = useState<string | null>(null);
  // Opened around an older message (search jump): newer pages load at the bottom.
  const detached = Boolean(timeline?.hasMoreAfter);

  const pending = useMemo(() => Object.values(pendingAll).filter((p) => p.conversationId === conversation.id), [pendingAll, conversation.id]);
  const built = useMemo(
    () =>
      buildTimeline({
        messages: timeline?.messages ?? [],
        pending,
        meId,
        kind: conversation.kind,
        unreadAnchorSeq: timeline?.unreadAnchorSeq ?? null,
        replyFor: (id) => client.replyRefFor(conversation.id, id),
        pendingReactions,
      }),
    // The messages, not the whole timeline object: its event cursor moves without anything to draw.
    [timeline?.messages, timeline?.unreadAnchorSeq, pending, meId, conversation.kind, conversation.id, client, pendingReactions],
  );
  // A line that reads the same as before keeps its object, so only the rows that changed are drawn again: the list is
  // rebuilt whole when anything changes (a receipt, a send, a reconnect), and every row was redrawn each time.
  const drawn = useRef(new Map<string, { json: string; item: TimelineItem }>());
  const items = useMemo(() => {
    const next = new Map<string, { json: string; item: TimelineItem }>();
    const out = built.map((item) => {
      let json: string;
      try {
        json = JSON.stringify(item);
      } catch {
        return item;
      }
      const before = drawn.current.get(item.key);
      const kept = before && before.json === json ? before.item : item;
      next.set(item.key, { json, item: kept });
      return kept;
    });
    drawn.current = next;
    return out;
  }, [built]);
  // The same names as one array as long as they stay the same: any profile change elsewhere renews `people`, and a new
  // array here drew every message row again.
  const mentionKey = useMemo(() => {
    const names = new Set<string>();
    for (const m of details?.members ?? []) names.add(m.person.displayName);
    for (const p of conversation.memberPreview) names.add(p.displayName);
    if (people[meId]) names.add(people[meId]!.displayName);
    return [...names].join('\n');
  }, [details, conversation.memberPreview, people, meId]);
  const mentionNames = useMemo(() => (mentionKey ? mentionKey.split('\n') : []), [mentionKey]);
  const lookup = useCallback((id: string | null | undefined) => (id ? people[id] : undefined), [people]);

  // The list is not a live region (it would read out history pages, own sends and receipt changes):
  // messages from others that arrive at the bottom go to this log instead, one short line each.
  const [arrivals, setArrivals] = useState<{ key: string; text: string }[]>([]);
  const newestSeen = useRef<{ seq: Seq | null; attached: boolean } | null>(null);
  useEffect(() => {
    if (!timeline?.loaded) return;
    const seen = newestSeen.current;
    newestSeen.current = { seq: timeline.messages[timeline.messages.length - 1]?.seq ?? null, attached: !timeline.hasMoreAfter };
    if (!seen?.attached || timeline.hasMoreAfter) return;
    const fresh = timeline.messages.filter((m) => (seen.seq === null || compareSeq(m.seq, seen.seq) > 0) && m.kind === 'text' && m.senderId !== meId && !m.deletedAt);
    if (!fresh.length) return;
    const lines = fresh.length > 3 ? [{ key: fresh[fresh.length - 1]!.id, text: `새 메시지 ${fresh.length}개` }] : fresh.map((m) => ({ key: m.id, text: arrivalText(m, lookup) }));
    setArrivals((list) => [...list, ...lines].slice(-5));
  }, [timeline, meId, lookup]);

  // On phones the list or the info panel covers the conversation: it is not being read then.
  const onScreen = !shell.single || shell.view === 'chat';
  const report = useCallback(() => {
    const el = box.current;
    if (!el) return;
    // The list got shorter or taller under a reader at the newest message (a phone keyboard coming up, a turned phone):
    // they stay there, whatever scroll events came on the way. On a phone the newest messages ended up behind the keyboard.
    if (el.clientHeight !== boxHeight.current) {
      const resized = boxHeight.current !== 0;
      boxHeight.current = el.clientHeight;
      if (resized && atBottom.current && positioned.current && !timeline?.hasMoreAfter) el.scrollTop = el.scrollHeight;
    }
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distance <= 1) gliding.current = false;
    atBottom.current = gliding.current || (distance < 80 && Date.now() >= seeking.current);
    setFar(!gliding.current && distance > 220);
    client.setViewing(onScreen ? conversation.id : null, atBottom.current);
    if (el.scrollTop < 120 && timeline?.hasMoreBefore && !timeline.loading) void client.loadOlder(conversation.id);
    if (distance < 120 && timeline?.hasMoreAfter && !timeline.loading) void client.loadNewer(conversation.id);
  }, [client, conversation.id, onScreen, timeline?.hasMoreBefore, timeline?.hasMoreAfter, timeline?.loading]);

  /** Brings the newest message into view: a short glide when it is near, a jump from further up. */
  const toNewest = useCallback(
    (el: HTMLDivElement) => {
      const gap = el.scrollHeight - el.clientHeight - el.scrollTop;
      if (gap <= 1) return;
      const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
      // A few pixels (a status line that changed) are not worth a glide.
      if (gap < 24 || gap > el.clientHeight || still || typeof el.scrollTo !== 'function') {
        el.scrollTop = el.scrollHeight;
        return;
      }
      gliding.current = true;
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
      // Not every browser reports the end of a smooth scroll (scrollend): stop counting it as one after a while.
      window.clearTimeout(glideTimer.current);
      glideTimer.current = window.setTimeout(() => {
        gliding.current = false;
        report();
      }, 900);
    },
    [report],
  );
  /** The reader took over (wheel, touch, keys, the scroll bar): a glide on its way no longer holds the bottom. */
  const stopGlide = () => {
    gliding.current = false;
    seeking.current = 0;
  };
  useEffect(() => () => window.clearTimeout(glideTimer.current), []);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const first = firstContentKey(items);
    const keys = items.flatMap((item) => (item.kind === 'message' ? [item.key] : []));
    // A message one has just sent shows even when reading further up.
    const fresh = new Set(arrivedAtBottom(known.current, keys));
    const sent = items.some((item) => item.kind === 'message' && fresh.has(item.key) && item.message.mine && !item.message.id);
    // A search hit is shown once, before measuring, as soon as its row is rendered; new messages must not pull the view back.
    const hit = flash && flashed.current !== flash.nonce ? el.querySelector<HTMLElement>(`.row[data-mid="${CSS.escape(flash.messageId)}"]`) : null;
    if (flash && hit) {
      flashed.current = flash.nonce;
      positioned.current = true;
      // A message a screen or two away (a reply's original) is glided to; one further away is jumped to.
      const away = Math.abs(hit.getBoundingClientRect().top - el.getBoundingClientRect().top);
      const glide = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches !== true && away < el.clientHeight * 2;
      gliding.current = false;
      if (glide) seeking.current = Date.now() + 900;
      hit.scrollIntoView({ block: 'center', behavior: glide ? 'smooth' : 'auto' });
      const target = hit.querySelector<HTMLElement>('.bubble,.fcard,.imgs');
      target?.classList.add('flash');
      window.setTimeout(() => target?.classList.remove('flash'), 1700);
    } else if (!positioned.current) {
      if (timeline?.loaded) {
        positioned.current = true;
        // Open where reading stopped: opening a conversation alone must not mark its backlog read.
        const divider = openingPosition(items) === 'unread' ? el.querySelector<HTMLElement>('.unread-div') : null;
        el.scrollTop = divider ? el.scrollTop + divider.getBoundingClientRect().top - el.getBoundingClientRect().top - DIVIDER_GAP_PX : el.scrollHeight;
      }
    } else if (toBottom.current && !detached) {
      toBottom.current = false;
      el.scrollTop = el.scrollHeight;
    } else if (firstKey.current && first !== firstKey.current && el.scrollTop < 200) {
      // Older page prepended: keep the reader where they were.
      el.scrollTop += el.scrollHeight - prevHeight.current;
    } else if ((atBottom.current || sent) && !detached) {
      toNewest(el);
    }
    firstKey.current = first;
    prevHeight.current = el.scrollHeight;
    if (positioned.current) known.current = new Set(keys);
    for (const child of el.children) resizes.current?.observe(child);
    report();
  }, [items, flash, timeline?.loaded, detached, report, toNewest]);

  // Link previews, pictures and reactions grow a row after it is drawn, and a phone keyboard shortens the list:
  // at the bottom, the newest message stays in view.
  const follow = useRef(() => {});
  follow.current = () => {
    const el = box.current;
    if (!el || !positioned.current || !atBottom.current || detached) return;
    // A glide on its way is pointed at the new bottom; otherwise the list keeps its place at once.
    if (gliding.current) toNewest(el);
    else el.scrollTop = el.scrollHeight;
  };
  // Some phone browsers resize the page for the keyboard in steps: once it settles, the newest message is in view again.
  useEffect(() => {
    const again = () => window.requestAnimationFrame(() => follow.current());
    window.addEventListener('resize', again);
    window.visualViewport?.addEventListener('resize', again);
    return () => {
      window.removeEventListener('resize', again);
      window.visualViewport?.removeEventListener('resize', again);
    };
  }, []);
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => follow.current());
    observer.observe(el);
    for (const child of el.children) observer.observe(child);
    resizes.current = observer;
    return () => {
      resizes.current = null;
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const onFocus = () => report();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [report]);

  // Stop counting as "viewing" only when this conversation leaves the screen; tying this to
  // `report` would clear it after every loading toggle, right after the layout effect set it.
  useEffect(() => () => client.setViewing(null, false), [client, conversation.id]);

  const unreadCount = unreadAfterAnchor(timeline?.messages ?? [], timeline?.unreadAnchorSeq ?? null, meId);
  const windowMs = WINDOW_MS[editWindow];
  const editLimit = editWindowText(editWindow);
  const rowKeys = items.flatMap((item) => (item.kind === 'message' ? [item.key] : []));
  for (const key of arrivedAtBottom(known.current, rowKeys)) if (!detached) arrived.current.add(key);
  const currentKey = focusKey !== null && rowKeys.includes(focusKey) ? focusKey : rowKeys[rowKeys.length - 1];

  return (
    <>
      <div className="sr" role="log" aria-live="polite" aria-label="새 메시지 알림">
        {arrivals.map((line) => (
          <p key={line.key}>{line.text}</p>
        ))}
      </div>
      <div className="msgs scroll" ref={box} onScroll={report} onWheel={stopGlide} onTouchStart={stopGlide} onPointerDown={stopGlide} onKeyDown={stopGlide} role="region" aria-label="메시지">
        {timeline?.loading && !timeline.loaded ? <div className="empty-note">메시지를 불러오는 중…</div> : null}
        {items.map((item) => {
          switch (item.kind) {
            case 'date':
              return (
                <div key={item.key} className="divider">
                  <span>{formatDayDivider(item.at, env.now())}</span>
                </div>
              );
            case 'system':
              return (
                <div key={item.key} className="sysmsg" data-mid={item.messageId}>
                  <span>
                    <Icon name={systemIcon(item.event)} size="sm" />
                    {systemText(item.event, lookup, meId, conversation.peer?.id)}
                  </span>
                </div>
              );
            case 'unread':
              return (
                <div key={item.key} className="unread-div">
                  여기서부터 안 읽은 메시지
                </div>
              );
            case 'message':
              return (
                <MessageRow
                  key={item.key}
                  item={item}
                  conversation={conversation}
                  mentionNames={mentionNames}
                  editWindowMs={windowMs}
                  editLimit={editLimit}
                  current={item.key === currentKey}
                  arrived={arrived.current.has(item.key)}
                  onFocusRow={setFocusKey}
                />
              );
          }
        })}
      </div>
      <button
        type="button"
        className={`newmsg${far || detached ? ' show' : ''}`}
        onClick={() => {
          if (detached) {
            toBottom.current = true;
            client.showLatest(conversation.id).catch((error: unknown) => {
              toBottom.current = false;
              toast(errorMessage(error));
            });
            return;
          }
          box.current?.scrollTo({ top: box.current.scrollHeight, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }}
      >
        <Icon name="arrowdown" size="sm" />
        <span>{unreadCount ? `새 메시지 ${unreadCount}개` : '최근 메시지로'}</span>
      </button>
    </>
  );
}
